/** EDP-00004 — Universal Workspace Architecture framework (mirrors pkg/workspaceshell) */

export const EDP_WS_ID = 'EDP-00004';

export type Philosophy =
  | 'workspace_first'
  | 'context_before_actions'
  | 'ai_always_available'
  | 'insights_before_reports'
  | 'explain_before_execute'
  | 'consistency_over_creativity'
  | 'minimal_navigation'
  | 'maximum_context';

export type WorkspaceType =
  | 'executive'
  | 'operational'
  | 'explorer'
  | 'editor'
  | 'analytics'
  | 'planning'
  | 'approval'
  | 'investigation'
  | 'ai_conversation'
  | 'administration'
  | 'configuration';

export type PageRegion =
  | 'global_header'
  | 'breadcrumb'
  | 'workspace_header'
  | 'workspace_toolbar'
  | 'context_banner'
  | 'primary_navigation'
  | 'secondary_navigation'
  | 'content_area'
  | 'ai_panel'
  | 'insights_panel'
  | 'recommendation_panel'
  | 'timeline'
  | 'activity_feed'
  | 'footer_utilities';

export type ViewMode =
  | 'grid'
  | 'table'
  | 'card'
  | 'timeline'
  | 'kanban'
  | 'calendar'
  | 'split'
  | 'comparison'
  | 'presentation'
  | 'tree';

export type ObjectDetailTab =
  | 'overview'
  | 'properties'
  | 'relationships'
  | 'recommendations'
  | 'timeline'
  | 'audit'
  | 'attachments'
  | 'ai_summary'
  | 'knowledge_links';

export interface WorkspaceTemplate {
  type: WorkspaceType;
  label: string;
  description?: string;
  primary_pattern?: string;
  modules?: string[];
  default_views?: ViewMode[];
}

export interface TimelineEvent {
  id: string;
  type: string;
  title: string;
  description?: string;
  actor?: string;
  module?: string;
  timestamp: string;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'workspace_first', label: 'Workspace First', description: 'User always knows where they are in the workspace model' },
  { id: 'context_before_actions', label: 'Context Before Actions', description: 'Scope and breadcrumbs before toolbar actions' },
  { id: 'ai_always_available', label: 'AI Always Available', description: 'Copilot or AI panel on every workspace' },
  { id: 'insights_before_reports', label: 'Insights Before Reports', description: 'Inline KPIs; reports are drill-down' },
  { id: 'explain_before_execute', label: 'Explain Before Execute', description: 'Preview before consequential actions' },
  { id: 'consistency_over_creativity', label: 'Consistency Over Creativity', description: 'Use shell components; custom layouts need UX review' },
  { id: 'minimal_navigation', label: 'Minimal Navigation', description: 'Tabs over pages; command palette for power users' },
  { id: 'maximum_context', label: 'Maximum Context', description: 'Explorer, insights, timeline visible without navigation' },
];

export const PAGE_REGIONS: { id: PageRegion; label: string }[] = [
  { id: 'global_header', label: 'Global Header' },
  { id: 'breadcrumb', label: 'Breadcrumb' },
  { id: 'workspace_header', label: 'Workspace Header' },
  { id: 'workspace_toolbar', label: 'Workspace Toolbar' },
  { id: 'context_banner', label: 'Context Banner' },
  { id: 'primary_navigation', label: 'Primary Navigation' },
  { id: 'secondary_navigation', label: 'Secondary Navigation' },
  { id: 'content_area', label: 'Content Area' },
  { id: 'ai_panel', label: 'AI Panel' },
  { id: 'insights_panel', label: 'Insights Panel' },
  { id: 'recommendation_panel', label: 'Recommendation Panel' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'activity_feed', label: 'Activity Feed' },
  { id: 'footer_utilities', label: 'Footer Utilities' },
];

export const WORKSPACE_TEMPLATES: WorkspaceTemplate[] = [
  { type: 'executive', label: 'Executive Workspace', description: 'Command center overview', primary_pattern: 'executive-dashboard', modules: ['commandcenter', 'biops'], default_views: ['card', 'presentation'] },
  { type: 'operational', label: 'Operational Workspace', description: 'Day-to-day operations', primary_pattern: 'explorer-detail', modules: ['catalogops', 'orderops', 'fbaops'], default_views: ['table', 'kanban'] },
  { type: 'explorer', label: 'Explorer Workspace', description: 'Hierarchy navigation', primary_pattern: 'tree-table', modules: ['amazoncatalog'], default_views: ['table', 'tree'] },
  { type: 'editor', label: 'Editor Workspace', description: 'Inline editing with AI', primary_pattern: 'split-editor', modules: ['aicatalog', 'aicreative'], default_views: ['split'] },
  { type: 'analytics', label: 'Analytics Workspace', description: 'KPIs and drill-down', primary_pattern: 'dashboard-tabs', modules: ['analytics', 'revenueops', 'finintel'], default_views: ['grid', 'presentation'] },
  { type: 'planning', label: 'Planning Workspace', description: 'Strategic planning', primary_pattern: 'planning-board', modules: ['growthplanner'], default_views: ['timeline', 'calendar'] },
  { type: 'approval', label: 'Approval Workspace', description: 'Governed review queue', primary_pattern: 'approval-queue', modules: ['publishing', 'governance'], default_views: ['table', 'comparison'] },
  { type: 'investigation', label: 'Investigation Workspace', description: 'Root-cause analysis', primary_pattern: 'timeline-first', modules: ['financeops'], default_views: ['timeline', 'split'] },
  { type: 'ai_conversation', label: 'AI Conversation', description: 'Copilot-first', primary_pattern: 'copilot-primary', modules: ['copilot'], default_views: ['split'] },
  { type: 'administration', label: 'Administration', description: 'Platform config', primary_pattern: 'settings-tabs', modules: ['administration'], default_views: ['table'] },
  { type: 'configuration', label: 'Configuration', description: 'Integration setup', primary_pattern: 'wizard-tabs', modules: ['integration'], default_views: ['table', 'card'] },
];

export const VIEW_MODES: { id: ViewMode; label: string }[] = [
  { id: 'grid', label: 'Grid' },
  { id: 'table', label: 'Table' },
  { id: 'card', label: 'Card' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'kanban', label: 'Kanban' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'split', label: 'Split' },
  { id: 'comparison', label: 'Comparison' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'tree', label: 'Tree' },
];

export const OBJECT_DETAIL_TABS: { id: ObjectDetailTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'properties', label: 'Properties' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'recommendations', label: 'Recommendations' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'audit', label: 'Audit' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'ai_summary', label: 'AI Summary' },
  { id: 'knowledge_links', label: 'Knowledge Links' },
];

export const TOOLBAR_ACTIONS = [
  'Create', 'Edit', 'Delete', 'Clone', 'Import', 'Export', 'Refresh',
  'Bulk Actions', 'Filters', 'Views', 'Saved Layouts', 'Command Palette',
];

export const DEMO_TIMELINE: TimelineEvent[] = [
  { id: 'tl-1', type: 'ai_decision', title: 'AI recommended budget increase', description: 'SP-EARBUDS-BRAND +20%', actor: 'copilot', module: 'revenueops', timestamp: new Date(Date.now() - 2 * 3600000).toISOString() },
  { id: 'tl-2', type: 'approval', title: 'Reimbursement claim approved', description: '$89.97 recovered', actor: 'finance.manager', module: 'financeops', timestamp: new Date(Date.now() - 5 * 3600000).toISOString() },
  { id: 'tl-3', type: 'sync', title: 'Catalog sync completed', description: '142 SKUs updated', actor: 'system', module: 'amazonsync', timestamp: new Date(Date.now() - 8 * 3600000).toISOString() },
  { id: 'tl-4', type: 'release', title: 'Listing update published', description: 'CASE-PRO title optimization', actor: 'listing.manager', module: 'publishing', timestamp: new Date(Date.now() - 24 * 3600000).toISOString() },
];

export function buildShellDashboard() {
  return {
    workspace_types: WORKSPACE_TEMPLATES.length,
    page_regions: PAGE_REGIONS.length,
    view_modes: VIEW_MODES.length,
    toolbar_actions: TOOLBAR_ACTIONS.length,
    object_detail_tabs: OBJECT_DETAIL_TABS.length,
  };
}

export function resolveTemplate(module: string): WorkspaceTemplate | undefined {
  return WORKSPACE_TEMPLATES.find((t) => t.modules?.includes(module));
}
